import { useRef, useState } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { DateTime } from 'luxon';
import { AddOnStatus, JobEventType, JobStatus } from '../../../__generated__/graphql';
import { Button } from '../../components/button';
import { Modal } from '../../components/modal';
import { OutstandingCharges } from '../../components/OutstandingCharges';
import {
  Alert,
  Badge,
  Card,
  CardBody,
  CardHeader,
  Field,
  InlineRow,
  Input,
  Muted,
  Select,
  Stack,
  Strong,
  Textarea,
} from '../../components/ui';
import { useStripePromise } from '../../hooks/useStripePromise';
import {
  useConfirmHandoverCapExtensionMutation,
  useConfirmJobCompletionMutation,
  useDisputeJobMutation,
  useExtendHandoverCapMutation,
  useGetPresignedUrlForJobPhotoMutation,
  useJobTimelineQuery,
  useRequestJobPhotoArchiveMutation,
  useRequestJobRefundMutation,
  useRespondJobAddOnMutation,
} from '../../services/serviceService/__generated__/queries.generated';
import { formatMoney } from '../ServiceProviderPage/priceTables';
import { HandoverClock } from '../ServiceJobPage/HandoverClock';
import { JobTimelineView, PhotoGridView } from '../ServiceJobPage/JobTimelineView';
import { ServiceReviewCard } from '../ServiceJobPage/ServiceReviewCard';
import {
  ADD_ON_STATUS_LABELS,
  pendingAddOns,
  canAskRefund,
  canConfirm,
  canDispute,
  handoverCapOptions,
  issueOf,
  liveEvents,
  minutesLabel,
  type JobAddOn,
} from '../ServiceJobPage/jobTimeline';
import { uploadJobPhotos } from '../ServiceJobPage/jobPhotos';
import { stayErrorMessage } from '../StayHostPage/stayErrors';
import type { ServiceJob } from './serviceJob';

const HiddenFileInput = styled.input`
  ${tw`hidden`}
`;

function useConfirmCard() {
  const { stripePromise } = useStripePromise();
  return async (clientSecret: string) => {
    const stripe = await stripePromise;
    if (!stripe) throw new Error('Payments are not ready yet. Please try again.');
    const { error } = await stripe.confirmCardPayment(clientSecret);
    if (error) throw new Error(error.message || 'The bank did not approve this hold.');
  };
}

/** D9: extras the worker asked for on site; approving may need a new hold (bank check). */
function AddOnsCard({ addOns }: { addOns: readonly JobAddOn[] }) {
  const [respond] = useRespondJobAddOnMutation({
    refetchQueries: ['JobTimeline', 'ServiceJobAsCustomer'],
  });
  const confirmCard = useConfirmCard();
  const [busy, setBusy] = useState<string | null>(null);
  if (!addOns.length) return null;

  const answer = async (addOn: JobAddOn, approve: boolean) => {
    setBusy(`${addOn.id}:${approve}`);
    try {
      const { data } = await respond({ variables: { addOnId: addOn.id, approve } });
      const secret = data?.respondJobAddOn.clientSecret;
      if (approve && secret) {
        await confirmCard(secret);
        await respond({ variables: { addOnId: addOn.id, approve: true } });
      }
      toast.success(approve ? 'Extra approved' : 'Extra declined');
    } catch (e) {
      toast.error(stayErrorMessage(e, 'Could not answer. Try again.'), { autoClose: 8000 });
    } finally {
      setBusy(null);
    }
  };

  return (
    <Card data-testid="job-add-ons">
      <CardHeader
        title={
          pendingAddOns(addOns).length
            ? 'Extras waiting for your answer'
            : 'Extras asked for on site'
        }
        description="Up to 25% of the job is approved automatically from the hold; anything above needs your yes."
      />
      <CardBody>
        <Stack $gap={6}>
          {addOns.map(addOn => (
            <Stack key={addOn.id} $gap={4}>
              <InlineRow style={{ justifyContent: 'space-between', flexWrap: 'wrap' }}>
                <Strong>
                  {addOn.label}
                  {addOn.quantity > 1 ? ` × ${addOn.quantity}` : ''} · {formatMoney(addOn.amount)}
                </Strong>
                <Badge tone={addOn.status === AddOnStatus.Requested ? 'warning' : 'neutral'}>
                  {ADD_ON_STATUS_LABELS[addOn.status]}
                </Badge>
              </InlineRow>
              <PhotoGridView urls={addOn.photoUrls} />
              {addOn.receiptUrl && (
                <a href={addOn.receiptUrl} target="_blank" rel="noreferrer">
                  Receipt
                </a>
              )}
              {addOn.status === AddOnStatus.Requested && (
                <InlineRow>
                  <Button
                    type="button"
                    size="sm"
                    text="Approve"
                    isLoading={busy === `${addOn.id}:true`}
                    onClick={() => answer(addOn, true)}
                    dataTestId={`addon-approve-${addOn.id}`}
                  />
                  <Button
                    type="button"
                    theme="secondary"
                    size="sm"
                    text="Decline"
                    isLoading={busy === `${addOn.id}:false`}
                    onClick={() => answer(addOn, false)}
                  />
                  <Muted $size="sm">
                    Answer by {DateTime.fromISO(addOn.respondBy).toFormat('ccc h:mm a')}
                  </Muted>
                </InlineRow>
              )}
            </Stack>
          ))}
        </Stack>
      </CardBody>
    </Card>
  );
}

/** D8, D30: a longer handover limit, held on the card now. */
function HandoverCapCard({ job }: { job: ServiceJob }) {
  const current = job.serviceJob?.maxBillableMinutes ?? 0;
  const options = handoverCapOptions(current);
  const [minutes, setMinutes] = useState<number>(options[3] ?? options[0] ?? 0);
  const [extend, { loading }] = useExtendHandoverCapMutation();
  const [confirm] = useConfirmHandoverCapExtensionMutation();
  const confirmCard = useConfirmCard();
  const [error, setError] = useState<string | null>(null);
  if (!options.length) return null;

  const submit = async () => {
    setError(null);
    try {
      const { data } = await extend({
        variables: { bookingId: job.id, maxBillableMinutes: minutes },
      });
      const answer = data?.extendHandoverCap;
      if (!answer) return;
      if (answer.status === 'REQUIRES_ACTION' && answer.clientSecret && answer.paymentIntentId) {
        await confirmCard(answer.clientSecret);
        await confirm({
          variables: { bookingId: job.id, paymentIntentId: answer.paymentIntentId },
        });
      } else if (answer.status === 'FAILED') {
        setError(answer.errorMessage ?? 'The extra time was not held. Try another card.');
        return;
      }
      toast.success(`Handover limit is now ${minutesLabel(minutes)}`);
    } catch (e) {
      setError(stayErrorMessage(e, 'The limit was not changed'));
    }
  };

  return (
    <Card data-testid="handover-cap">
      <CardHeader
        title="Handover time limit"
        description={`Now ${minutesLabel(current)}. A longer limit holds the extra amount on your card; you pay only for the time used.`}
      />
      <CardBody>
        <Stack $gap={4}>
          <InlineRow>
            <Select
              aria-label="New limit"
              value={String(minutes)}
              onChange={e => setMinutes(Number(e.target.value))}
              style={{ width: 180 }}
            >
              {options.map(m => (
                <option key={m} value={m}>
                  {minutesLabel(m)}
                </option>
              ))}
            </Select>
            <Button
              type="button"
              theme="secondary"
              text="Extend"
              onClick={submit}
              isLoading={loading}
              dataTestId="handover-extend"
            />
          </InlineRow>
          {error && (
            <Alert tone="error" title="Not extended">
              {error}
            </Alert>
          )}
        </Stack>
      </CardBody>
    </Card>
  );
}

function ReportModal({
  job,
  kind,
  onClose,
}: {
  job: ServiceJob;
  kind: 'dispute' | 'refund';
  onClose: () => void;
}) {
  const [dispute, { loading: disputing }] = useDisputeJobMutation();
  const [refund, { loading: refunding }] = useRequestJobRefundMutation();
  const [presign] = useGetPresignedUrlForJobPhotoMutation();
  const [files, setFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const [note, setNote] = useState('');
  const [amount, setAmount] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [claimId, setClaimId] = useState<string | null>(null);
  const paid = Number(job.receipt.finalTotal ?? job.receipt.totalPrice ?? 0);

  const submit = async () => {
    if (!note.trim()) {
      setError('Tell us what is wrong');
      return;
    }
    setError(null);
    const value = Number(amount);
    if (kind === 'refund' && (!(value > 0) || value > paid)) {
      setError(`Enter an amount up to ${formatMoney(paid)}`);
      return;
    }
    try {
      let photoKeys: string[] | undefined;
      if (files.length) {
        setUploading(true);
        try {
          photoKeys = await uploadJobPhotos(files, async (contentType, contentLength) => {
            const { data } = await presign({
              variables: { bookingId: job.id, contentType, contentLength },
            });
            if (!data?.getPresignedUrlForJobPhoto) throw new Error('The upload could not start');
            return data.getPresignedUrlForJobPhoto;
          });
        } finally {
          setUploading(false);
        }
      }
      if (kind === 'dispute') {
        await dispute({ variables: { bookingId: job.id, note: note.trim(), photoKeys } });
        toast.success('Sent for review. You are not charged until it is settled.');
        onClose();
      } else {
        const { data } = await refund({
          variables: { bookingId: job.id, amount: value, note: note.trim(), photoKeys },
        });
        setClaimId(data?.requestJobRefund.id ?? null);
      }
    } catch (e) {
      setError(stayErrorMessage(e, 'Not sent. Try again.'));
    }
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={kind === 'dispute' ? 'Report a problem' : 'Ask for a refund'}
    >
      <Stack $gap={4}>
        <Strong>
          {kind === 'dispute' ? 'Report a problem with this job' : 'Ask for a refund'}
        </Strong>
        {claimId ? (
          <Alert tone="success" title="Request sent">
            Follow it in the <Link to="/guest/damage-claims">Resolution Center</Link>.
          </Alert>
        ) : (
          <>
            <Muted $size="sm">
              {kind === 'dispute'
                ? 'We hold the payment while our team looks at the photos and your note.'
                : 'Up to 3 days after you confirmed the job. The Provider can answer before our team decides.'}
            </Muted>
            {kind === 'refund' && (
              <Field label="Amount" htmlFor="refund-amount" hint={`Up to ${formatMoney(paid)}`}>
                <Input
                  id="refund-amount"
                  inputMode="decimal"
                  value={amount}
                  onChange={e => setAmount(e.target.value)}
                />
              </Field>
            )}
            <Field label="What is wrong?" htmlFor="report-note" required>
              <Textarea
                id="report-note"
                rows={4}
                maxLength={2000}
                value={note}
                onChange={e => setNote(e.target.value)}
                data-testid="report-note"
              />
            </Field>
            <HiddenFileInput
              ref={fileInput}
              type="file"
              accept="image/*"
              multiple
              onChange={e => setFiles(Array.from(e.target.files ?? []).slice(0, 10))}
              data-testid="report-photos"
            />
            <InlineRow>
              <Button
                type="button"
                theme="secondary"
                size="sm"
                text="Add photos"
                onClick={() => fileInput.current?.click()}
              />
              <Muted $size="sm">
                {files.length ? `${files.length} photo(s)` : 'Optional, up to 10'}
              </Muted>
            </InlineRow>
            {error && (
              <Alert tone="error" title="Not sent">
                {error}
              </Alert>
            )}
            <InlineRow>
              <Button
                type="button"
                text="Send"
                onClick={submit}
                isLoading={uploading || disputing || refunding}
                dataTestId="report-send"
              />
              <Button type="button" theme="text" text="Cancel" onClick={onClose} />
            </InlineRow>
          </>
        )}
      </Stack>
    </Modal>
  );
}

/**
 * The customer's job page after booking (spec 019 W21): unpaid charges, the
 * extras to answer, confirm or report a problem, a refund request, a longer
 * handover limit, the timeline with photos and the photo download.
 */
export function CustomerJobSections({ job }: { job: ServiceJob }) {
  const info = job.serviceJob!;
  const timelineQuery = useJobTimelineQuery({
    variables: { bookingId: job.id },
    fetchPolicy: 'cache-and-network',
  });
  const [confirmJob, { loading: confirming }] = useConfirmJobCompletionMutation();
  const [archive, { loading: zipping }] = useRequestJobPhotoArchiveMutation();
  const [report, setReport] = useState<'dispute' | 'refund' | null>(null);
  const timeline = timelineQuery.data?.jobTimeline ?? null;
  const jobStatus = timeline?.jobStatus ?? info.jobStatus;
  const events = timeline ? liveEvents(timeline.events) : [];
  const issues = events.map(issueOf).filter(Boolean) as { label: string; note: string | null }[];
  const hasPhotos = events.some(e => e.photoUrls.length > 0);
  const handover =
    Array.isArray(info.items) &&
    (info.items as any[]).some(i => String(i?.serviceType).toLowerCase() === 'vehicle_handover');
  const done = jobStatus === JobStatus.CustomerConfirmed || jobStatus === JobStatus.Resolved;

  const onConfirm = async () => {
    try {
      await confirmJob({ variables: { bookingId: job.id } });
      toast.success('Thanks. The job is confirmed and paid.');
    } catch (e) {
      toast.error(stayErrorMessage(e, 'Could not confirm'));
    }
  };

  const onZip = async () => {
    try {
      const { data } = await archive({ variables: { bookingId: job.id } });
      if (data?.requestJobPhotoArchive)
        window.open(data.requestJobPhotoArchive, '_blank', 'noopener');
    } catch (e) {
      toast.error(stayErrorMessage(e, 'The download could not be made'));
    }
  };

  return (
    <Stack $gap={6}>
      <OutstandingCharges bookingId={job.id} />
      {timeline && <AddOnsCard addOns={timeline.addOns} />}
      {issues.length > 0 && (
        <Alert tone="warning" title="The worker reported a problem" data-testid="job-issues">
          {issues.map((issue, i) => (
            <div key={i}>
              {issue.label}
              {issue.note ? `: ${issue.note}` : ''}
            </div>
          ))}
        </Alert>
      )}
      {canConfirm(jobStatus) && (
        <Card data-testid="job-confirm">
          <CardHeader
            title="The worker finished"
            description="Check the photos. Confirm to pay, or report a problem. Without an answer the job is confirmed after 24 hours."
          />
          <CardBody>
            <InlineRow>
              <Button
                type="button"
                text="Confirm and pay"
                onClick={onConfirm}
                isLoading={confirming}
                dataTestId="job-confirm-button"
              />
              {canDispute(jobStatus) && (
                <Button
                  type="button"
                  theme="secondary"
                  text="Report a problem"
                  onClick={() => setReport('dispute')}
                  dataTestId="job-dispute"
                />
              )}
            </InlineRow>
          </CardBody>
        </Card>
      )}
      {jobStatus === JobStatus.Disputed && (
        <Alert tone="info" title="Under review">
          Our team is looking at this job. You are told the outcome by email.
        </Alert>
      )}
      {canAskRefund(jobStatus, info.refundWindowEndsAt) && (
        <Card>
          <CardHeader
            title="Something not right?"
            description={`You can ask for a refund until ${DateTime.fromISO(info.refundWindowEndsAt!).toFormat('ccc, LLL d h:mm a')}.`}
          />
          <CardBody>
            <Button
              type="button"
              theme="secondary"
              text="Ask for a refund"
              onClick={() => setReport('refund')}
              dataTestId="job-refund"
            />
          </CardBody>
        </Card>
      )}
      {handover && timeline && (
        <HandoverClock
          minutesSoFar={timeline.handoverMinutesSoFar}
          maxBillableMinutes={info.maxBillableMinutes}
        />
      )}
      {handover &&
        !done &&
        jobStatus !== JobStatus.Completed &&
        jobStatus !== JobStatus.Disputed && <HandoverCapCard job={job} />}
      <ServiceReviewCard
        bookingId={job.id}
        reviewer="customer"
        jobStatus={jobStatus}
        completedAt={info.completedAt}
        otherName={info.workerName ?? 'The worker'}
        zone={info.timezone}
      />
      {timeline && (
        <>
          {hasPhotos && (
            <div>
              <Button
                type="button"
                theme="secondary"
                size="sm"
                text="Download all photos"
                onClick={onZip}
                isLoading={zipping}
                dataTestId="job-photo-zip"
              />
            </div>
          )}
          <JobTimelineView timeline={timeline} zone={info.timezone} />
        </>
      )}
      {timeline?.events.some(e => e.type === JobEventType.AutoConfirmed) && (
        <Muted $size="sm">This job was confirmed automatically.</Muted>
      )}
      {report && <ReportModal job={job} kind={report} onClose={() => setReport(null)} />}
    </Stack>
  );
}
